<template>
  <b-container class="d-flex flex-column h-100" fluid>
    <div
      class="pagetitle-container d-flex justify-content-start align-items-center"
    >
      <div class="page-title">
        <h2>
          Notifikácie
          <small>({{ count }})</small>
        </h2>
      </div>
      <div class="d-flex flex-grow-1">
        <input-filter
          ref="filter"
          :filters="table.filter"
          :options="filterOptions"
          @onReset="resetFilter"
          @onSearch="search"
        >
          <template #filter>
            <b-row>
              <b-col md="4">
                <b-form-group label-cols-md="4" label-for="read">
                  <b-form-checkbox
                    v-model="table.filter.read"
                    :unchecked-value="0"
                    :value="1"
                    class="mr-2"
                    switch
                    @change="search"
                  >
                    Zobraziť len neprečítané
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="4">
                <b-form-group
                  label="Typ:"
                  label-align-md="right"
                  label-cols-md="4"
                  label-for="type"
                >
                  <b-input-group class="input-group-labeled">
                    <selectbox
                      v-model="table.filter.type"
                      :options="[
                        { id: 1, name: 'Dosiahnutie 85%' },
                        { id: 2, name: 'Prekročenie hranice' },
                        { id: 3, name: 'Nahrajte meranú spotrebu' },
                      ]"
                      :searchable="false"
                      @input="search"
                    />
                    <b-input-group-append>
                      <b-btn
                        size="sm"
                        variant="link"
                        @click="
                          table.filter.type = null
                          search()
                        "
                      >
                        <b-icon icon="x" scale="1.5x" />
                      </b-btn>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group
                  label="Dátum:"
                  label-align-md="right"
                  label-cols-md="4"
                  label-for="created_at"
                >
                  <b-input-group class="input-group-labeled">
                    <date-picker
                      v-model="table.filter.created_at"
                      class="flex-grow-1 daterange-sm"
                      opens="left"
                      @update="search"
                    />
                    <b-input-group-append>
                      <b-btn
                        size="sm"
                        variant="link"
                        @click="
                          table.filter.created_at = { ge: null, lt: null }
                          search()
                        "
                      >
                        <b-icon icon="x" scale="1.5x" />
                      </b-btn>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-row>
          </template>
        </input-filter>
      </div>
      <div class="ml-3">
        <b-dropdown
          class="mr-1"
          menu-class="dropdown-menu-animated"
          right
          size="lg"
          variant="secondary"
        >
          <template #button-content>
            <b-icon icon="gear" />
          </template>
          <b-dropdown-item-button
            v-if="$root.user.isAdmin"
            @click="exportNotifications"
          >
            <b-icon class="mr-1" icon="file-earmark-spreadsheet" />
            Exportovať
          </b-dropdown-item-button>
          <b-dropdown-item-button @click="markAllRead">
            <b-icon class="mr-1" icon="eye" />
            Označiť všetky ako prečítané
          </b-dropdown-item-button>
        </b-dropdown>
      </div>
    </div>
    <div ref="scroll" v-bar class="bar-out">
      <div>
        <server-table
          ref="table"
          :api-request="apiRequest"
          :columns="table.columns"
          :searchable="false"
          :sort-by.sync="table.sortBy"
          :sort-desc="true"
          bordered
          hover
          striped
          @refreshed="onLoaded"
        >
          <template #cell(actions)="data">
            <b-dropdown class="btn-p-0" no-caret variant="link">
              <template slot="button-content">
                <b-icon icon="text-indent-left" scale="1.25x" />
              </template>
              <b-dropdown-item
                v-if="!data.item.read_at"
                @click="markAsRead(data.item.id)"
              >
                <b-icon class="mr-1" icon="eye" />
                Označiť ako prečítané
              </b-dropdown-item>
              <b-dropdown-item
                v-if="data.item.read_at"
                @click="markAsUnread(data.item.id)"
              >
                <b-icon class="mr-1" icon="eye-slash" />
                Označiť ako neprečítané
              </b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-item
                :to="{
                  name: 'customerView',
                  params: { id: data.item.energetics.customer.id },
                }"
              >
                <b-icon class="mr-1" icon="person" />
                Prejsť na zákazníka
              </b-dropdown-item>
              <b-dropdown-item
                :to="{
                  name: 'customerEnergetics',
                  params: {
                    id: data.item.energetics.customer.id,
                    energetics_id: data.item.energetics.id,
                  },
                }"
              >
                <b-icon class="mr-1" icon="grid" />
                Prejsť na odberné miesto
              </b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-item
                :to="{
                  name: 'customerEnergeticTechnicalTab',
                  params: {
                    id: data.item.energetics.customer.id,
                    energetics_id: data.item.energetics.id,
                  },
                }"
              >
                <b-icon class="mr-1" icon="wrench" />
                Technické hodnoty
              </b-dropdown-item>
              <b-dropdown-item
                :to="{
                  name: 'customerEnergeticContractTab',
                  params: {
                    id: data.item.energetics.customer.id,
                    energetics_id: data.item.energetics.id,
                  },
                }"
              >
                <b-icon class="mr-1" icon="vector-pen" />
                Zmluvné hodnoty
              </b-dropdown-item>
              <b-dropdown-item
                :to="{
                  name: 'customerEnergeticDeviceTab',
                  params: {
                    id: data.item.energetics.customer.id,
                    energetics_id: data.item.energetics.id,
                  },
                }"
              >
                <b-icon class="mr-1" icon="hdd-network" />
                Prístroje
              </b-dropdown-item>
            </b-dropdown>
          </template>
          <template #cell(type)="data">
            <icon
              v-if="data.value === 'App\\Notifications\\OverPower'"
              v-b-tooltip.hover.right
              class="icon-fw text-power"
              name="regular/caret-square-up"
              scale="1.2"
              title="Prekročenie RK/MRK"
            />
            <icon
              v-if="data.value === 'App\\Notifications\\NearOverPower'"
              v-b-tooltip.hover.right
              class="icon-fw text-power"
              name="percentage"
              scale="1.2"
              title="Dosiahnuté 85% RK"
            />
            <icon
              v-if="data.value === 'App\\Notifications\\OverGas'"
              v-b-tooltip.hover.right
              class="icon-fw text-gas"
              name="regular/caret-square-up"
              scale="1.2"
              title="Prekročenie hranice"
            />
            <icon
              v-if="data.value === 'App\\Notifications\\NearOverGas'"
              v-b-tooltip.hover.right
              class="icon-fw text-gas"
              name="percentage"
              scale="1.2"
              title="Dosiahnuté 85% odberu"
            />
            <icon
              v-if="data.value === 'App\\Notifications\\UploadOperation'"
              v-b-tooltip.hover.right
              class="icon-fw text-power"
              name="sort-numeric-up"
              scale="1.2"
              title="Nahrajte meranú spotrebu"
            />
          </template>
          <template #cell(energetics)="data">
            <div class="text-nowrap">
              <template v-if="data.item.energetics._type.const === 'POWER'">
                <b-badge class="bg-power align-middle">
                  <icon class="icon-fw" name="plug" scale="0.75" />
                </b-badge>
                {{ data.item.energetics.powerNumber }}
              </template>
              <template v-if="data.item.energetics._type.const === 'GAS'">
                <b-badge class="bg-gas align-middle">
                  <icon class="icon-fw" name="fire" scale="0.75" />
                </b-badge>
                {{ data.item.energetics.gasNumber }}
              </template>
              <div class="fs-sm text-muted">
                {{ data.item.energetics.localityName }}
              </div>
            </div>
          </template>
          <template #cell(data)="data">
            <template v-if="data.item.type === 'App\\Notifications\\OverPower'">
              <div :class="{ 'font-weight-bolder': !data.item.read_at }">
                Dátum:
                {{ data.value.date | formatDate }}
                <br />
                <div
                  v-if="data.value.overflows && data.value.overflows.mrk > 0"
                >
                  Prekročenie MRK:
                  <span class="font-weight-bold">{{
                    data.value.overflows.mrk
                  }}</span
                  >x
                </div>
                <div v-if="data.value.overflows && data.value.overflows.rk > 0">
                  Prekročenie RK:
                  <span class="font-weight-bold">{{
                    data.value.overflows.rk
                  }}</span
                  >x
                </div>
              </div>
            </template>
            <template
              v-if="data.item.type === 'App\\Notifications\\NearOverPower'"
            >
              <div :class="{ 'font-weight-bolder': !data.item.read_at }">
                Dátum:
                {{ data.value.date | formatDate }}
                <br />
                <div>Dosiahnuté 85% RK</div>
              </div>
            </template>
            <template v-if="data.item.type === 'App\\Notifications\\OverGas'">
              <div :class="{ 'font-weight-bolder': !data.item.read_at }">
                Dátum:
                {{ data.value.date | formatDate }}
                <br />
                <div v-if="data.value.overLimitOver110percent > 0">
                  Prekročenie hranice 110% o:
                  <span class="font-weight-bold"
                    >{{
                      data.value.overLimitOver110percent | formatNumber(2)
                    }}
                    m3</span
                  >
                </div>
                <div v-if="data.value.overLimit105to110percent > 0">
                  Prekročenie hranice 105% o:
                  <span class="font-weight-bold"
                    >{{
                      data.value.overLimit105to110percent | formatNumber(2)
                    }}
                    m3</span
                  >
                </div>
                <div v-if="data.value.tolerance100to105percent > 0">
                  Prekročenie hranice 100% o:
                  <span class="font-weight-bold"
                    >{{
                      data.value.tolerance100to105percent | formatNumber(2)
                    }}
                    m3</span
                  >
                </div>
              </div>
            </template>
            <template
              v-if="data.item.type === 'App\\Notifications\\NearOverGas'"
            >
              <div :class="{ 'font-weight-bolder': !data.item.read_at }">
                Dátum:
                {{ data.value.date | formatDate }}
                <br />
                <div>Dosiahnuté 85% odberu</div>
              </div>
            </template>
            <template
              v-if="data.item.type === 'App\\Notifications\\UploadOperation'"
            >
              <div :class="{ 'font-weight-bolder': !data.item.read_at }">
                <div>Nahrajte meranú spotrebu podružných meradiel</div>
                Obdobie:
                {{ data.value.date | formatDate('MMM YYYY') }}
              </div>
            </template>
          </template>
        </server-table>
      </div>
    </div>
  </b-container>
</template>
<script>
import InputFilter from '@/components/InputFilter'
import Selectbox from '@/components/inputs/Selectbox'
import DatePicker from '@/components/inputs/DatePicker'
import ServerTable from '@/components/ServerTable'

export default {
  components: {
    InputFilter,
    Selectbox,
    DatePicker,
    ServerTable,
  },
  data() {
    return {
      table: {
        columns: [
          {
            key: 'actions',
            label: '',
            class: 'th-shrink',
          },
          {
            key: 'type',
            label: 'Typ',
            class: 'th-shrink',
            sortable: true,
          },
          {
            key: 'created_at',
            label: 'Dátum a čas',
            formatter: (value) =>
              value ? this.$options.filters.formatDateTime(value) : '-',
            sortable: true,
          },
          {
            key: 'customer',
            label: 'Názov spoločnosti',
            formatter: (value, key, item) =>
              item.energetics.customer.name || '-',
            sortable: true,
            sortableKey: 'customers.name',
            searchable: true,
          },
          {
            key: 'customerGroup',
            label: 'Skupina',
            formatter: (value, key, item) =>
              item.energetics.customer.group
                ? item.energetics.customer.group.name
                : '-',
            sortable: true,
            sortableKey: 'customer_groups.name',
            searchable: true,
          },
          {
            key: 'energetics',
            label: 'Odberné miesto',
            tdClass: 'text-nowrap',
            sortable: true,
            sortableKey: 'powerNumber, gasNumber, waterNumber, heatNumber',
            searchable: true,
          },
          {
            key: 'data',
            label: 'Opis',
          },
        ],
        sortBy: 'created_at',
        filter: {
          read: 1,
          type: null,
          created_at: { ge: null, lt: null },
        },
      },
      filterOptions: {
        placeholder: 'Filtrovanie a hľadanie...',
      },
      count: null,
    }
  },
  methods: {
    apiRequest(params) {
      return this.$api.get('notifications/get', {
        params: { filter: this.table.filter, ...params },
      })
    },
    onLoaded() {
      this.count = this.$refs.table.totalRows
    },
    search() {
      this.$refs.table.searchVal = this.$refs.filter.value
      this.$nextTick(() => {
        this.$refs.table.search()
      })
    },
    resetFilter() {
      this.$refs.table.reset()
    },
    exportNotifications() {
      this.$swal({
        toast: true,
        type: 'info',
        position: 'top-end',
        title: 'Pripravujem export...',
      })
      this.$api
        .get('notifications/export', {
          params: {
            query: this.$refs.filter.value,
            filter: this.table.filter,
            ...this.$refs.table.getRequestParams(),
            startAt: 0,
            maxResults: -1,
          },
          responseType: 'blob',
        })
        .then((res) => {
          saveAs(new Blob([res.data]), 'notifikacie.xlsx')
          this.$swal({
            toast: true,
            type: 'success',
            position: 'top-end',
            title: 'Export pripravený',
            timer: 5000,
          })
        })
        .catch(() => {
          this.$swal({
            toast: true,
            type: 'error',
            position: 'top-end',
            title: 'Chyba pri exportovaní',
            timer: 5000,
          })
        })
    },
    markAllRead() {
      this.$api.post('notifications/mark_all_read').then(() => {
        this.$refs.table.reset()
        this.$store.dispatch('user/fetchNotifications')
      })
    },
    markAsRead(id) {
      this.$api.post('notifications/mark_as_read', { id: id }).then(() => {
        this.$refs.table.reset()
        this.$store.dispatch('user/fetchNotifications')
      })
    },
    markAsUnread(id) {
      this.$api.post('notifications/mark_as_unread', { id: id }).then(() => {
        this.$refs.table.reset()
        this.$store.dispatch('user/fetchNotifications')
      })
    },
  },
}
</script>
